<template>
  <div class="wa-accounts-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader pageTitle="WhatsApp Accounts" />

      <main class="page-content">
        <div class="wa-accounts-view">

          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1 class="page-title">📱 WhatsApp Business Accounts</h1>
              <p class="page-subtitle">All WABAs assigned to this BSP</p>
            </div>
            <div class="header-stats">
              <div class="stat-card">
                <div class="stat-number">{{ accounts.length }}</div>
                <div class="stat-label">Total Accounts</div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading WhatsApp accounts...</p>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="error-container">
            <div class="error-icon">⚠️</div>
            <h3>Error Loading Accounts</h3>
            <p>{{ error }}</p>
            <button @click="loadAccounts" class="retry-button">Try Again</button>
          </div>

          <!-- Empty State -->
          <div v-else-if="accounts.length === 0" class="empty-state">
            <div class="empty-icon">📱</div>
            <h3>No Accounts Found</h3>
            <p>No WhatsApp Business Accounts are assigned to this BSP yet.</p>
          </div>

          <!-- Table -->
          <div v-else class="table-container">
            <div class="table-header">
              <h2 class="table-title">Accounts ({{ filtered.length }})</h2>
              <div class="table-actions">
                <div class="search-box">
                  <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                  </svg>
                  <input v-model="search" type="text" placeholder="Search by name or WABA ID..." class="search-input" />
                </div>
                <button @click="loadAccounts" class="refresh-button">
                  🔄 Refresh
                </button>
              </div>
            </div>

            <div class="table-wrapper desktop-view">
              <table class="accounts-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Account Name</th>
                    <th>WABA ID</th>
                    <th>Template Namespace</th>
                    <th>Currency</th>
                    <th>Timezone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(account, index) in filtered" :key="account.id" class="account-row">
                    <td class="index-cell">{{ index + 1 }}</td>
                    <td class="name-cell">
                      <div class="name-info">
                        <span class="account-name">{{ account.name }}</span>
                        <span class="account-id">#{{ account.id }}</span>
                      </div>
                    </td>
                    <td>
                      <span class="id-badge">{{ account.id }}</span>
                    </td>
                    <td>
                      <span class="namespace-text">{{ account.message_template_namespace || '—' }}</span>
                    </td>
                    <td>{{ account.currency || '—' }}</td>
                    <td>{{ account.timezone_id || '—' }}</td>
                    <td class="actions-cell">
                      <div class="action-buttons">
                        <button @click="openPhones(account)" class="action-btn phones-btn" title="View Phone Numbers">
                          📞
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile Cards -->
            <div class="mobile-view">
              <div v-for="account in filtered" :key="account.id" class="account-card">
                <div class="account-card-header">
                  <div class="account-name-info">
                    <h3 class="account-name">{{ account.name }}</h3>
                    <span class="account-id">#{{ account.id }}</span>
                  </div>
                  <div class="lead-actions">
                    <button @click="openPhones(account)" class="mobile-action-btn phones-btn" title="View Phone Numbers">
                      📞
                    </button>
                  </div>
                </div>
                <div class="account-card-body">
                  <div class="info-row">
                    <span class="info-label">WABA ID:</span>
                    <span class="id-badge">{{ account.id }}</span>
                  </div>
                  <div class="info-row" v-if="account.message_template_namespace">
                    <span class="info-label">Namespace:</span>
                    <span class="info-value namespace-text">{{ account.message_template_namespace }}</span>
                  </div>
                  <div class="info-row" v-if="account.currency">
                    <span class="info-label">Currency:</span>
                    <span class="info-value">{{ account.currency }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">Timezone:</span>
                    <span class="info-value">{{ account.timezone_id || '—' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>

    <!-- Phone Numbers Modal -->
    <div v-if="selectedAccount" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>📞 Phone Numbers — {{ selectedAccount.name }}</h3>
          <button @click="closeModal" class="close-button">✕</button>
        </div>

        <div class="modal-body">

          <!-- Loading -->
          <div v-if="phonesLoading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading phone numbers...</p>
          </div>

          <!-- Error -->
          <div v-else-if="phonesError" class="error-container inline-error">
            <div class="error-icon">⚠️</div>
            <h3>Failed to Load</h3>
            <p>{{ phonesError }}</p>
            <button @click="fetchPhones(selectedAccount)" class="retry-button">Try Again</button>
          </div>

          <!-- Empty -->
          <div v-else-if="phones.length === 0" class="empty-state inline-empty">
            <div class="empty-icon">📵</div>
            <h3>No Phone Numbers</h3>
            <p>No phone numbers are registered under this WABA.</p>
          </div>

          <!-- Phones table -->
          <div v-else>
            <div class="detail-group">
              <h4>Registered Numbers ({{ phones.length }})</h4>
              <div class="table-wrapper">
                <table class="phones-table">
                  <thead>
                    <tr>
                      <th>Phone Number</th>
                      <th>Verified Name</th>
                      <th>Verification</th>
                      <th>Quality</th>
                      <th>Throughput</th>
                      <th>Phone ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="phone in phones" :key="phone.id" class="phone-row">
                      <td class="phone-number-cell">{{ phone.display_phone_number }}</td>
                      <td>{{ phone.verified_name || '—' }}</td>
                      <td>
                        <span class="status-badge" :class="verificationClass(phone.code_verification_status)">
                          {{ formatStatus(phone.code_verification_status) }}
                        </span>
                      </td>
                      <td>
                        <span class="status-badge" :class="qualityClass(phone.quality_rating)">
                          {{ formatStatus(phone.quality_rating) }}
                        </span>
                      </td>
                      <td>{{ formatStatus(phone.throughput?.level) }}</td>
                      <td><span class="id-badge">{{ phone.id }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Webhook info per phone -->
            <div class="detail-group" v-for="phone in phones.filter(p => p.webhook_configuration?.phone_number)" :key="'wh-' + phone.id">
              <h4>Webhook — {{ phone.display_phone_number }}</h4>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>Phone Webhook:</label>
                  <span class="webhook-url">{{ phone.webhook_configuration.phone_number }}</span>
                </div>
                <div class="detail-item" v-if="phone.webhook_configuration.application">
                  <label>App Webhook:</label>
                  <span class="webhook-url">{{ phone.webhook_configuration.application }}</span>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="modal-footer">
          <button @click="closeModal" class="close-btn">Close</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import DashboardService from '@/services/dashboard.js'

export default {
  name: 'WhatsAppAccountsView',
  components: { Sidebar, TopHeader },
  data () {
    return {
      accounts: [],
      loading: false,
      error: null,
      search: '',
      selectedAccount: null,
      phones: [],
      phonesLoading: false,
      phonesError: null
    }
  },
  computed: {
    filtered () {
      if (!this.search.trim()) return this.accounts
      const q = this.search.toLowerCase()
      return this.accounts.filter(a =>
        (a.name || '').toLowerCase().includes(q) ||
        (a.id || '').includes(q)
      )
    }
  },
  created () {
    this.loadAccounts()
  },
  methods: {
    async loadAccounts () {
      this.loading = true
      this.error = null
      try {
        const res = await DashboardService.getClientWhatsAppAccounts()
        this.accounts = res.data.accounts || []
      } catch (err) {
        this.error = err.message || 'Failed to load WhatsApp accounts.'
      } finally {
        this.loading = false
      }
    },

    openPhones (account) {
      this.selectedAccount = account
      this.fetchPhones(account)
    },

    async fetchPhones (account) {
      this.phones = []
      this.phonesLoading = true
      this.phonesError = null
      try {
        this.phones = await DashboardService.getWabaPhoneNumbers(account.id)
      } catch (err) {
        this.phonesError = err.message || 'Failed to load phone numbers.'
      } finally {
        this.phonesLoading = false
      }
    },

    closeModal () {
      this.selectedAccount = null
      this.phones = []
      this.phonesError = null
    },

    formatStatus (val) {
      if (!val || val === 'NOT_APPLICABLE') return '—'
      return val.replace(/_/g, ' ')
    },

    verificationClass (status) {
      if (status === 'VERIFIED') return 'badge-green'
      if (status === 'NOT_VERIFIED') return 'badge-red'
      return 'badge-gray'
    },

    qualityClass (rating) {
      if (rating === 'GREEN') return 'badge-green'
      if (rating === 'YELLOW') return 'badge-yellow'
      if (rating === 'RED') return 'badge-red'
      return 'badge-gray'
    }
  }
}
</script>

<style scoped>
/* Layout */
.wa-accounts-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.wa-accounts-view {
  background: var(--bg-secondary);
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding: 32px;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.header-stats { display: flex; gap: 16px; }

.stat-card {
  background: var(--bg-secondary);
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid var(--border-primary);
  text-align: center;
  min-width: 100px;
}

.stat-number {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.inline-error { border-radius: 0; border: none; padding: 40px; }

.error-icon { font-size: 48px; margin-bottom: 16px; }
.error-container h3 { color: var(--text-primary); margin: 0 0 8px; }
.error-container p  { color: var(--text-secondary); margin: 0 0 24px; }

.retry-button {
  background: #667eea;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
}
.retry-button:hover { background: #5a67d8; }

/* Empty */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
}

.inline-empty { border-radius: 0; border: none; padding: 40px; }

.empty-icon { font-size: 48px; margin-bottom: 16px; }
.empty-state h3 { color: var(--text-primary); margin: 0 0 8px; }
.empty-state p  { color: var(--text-secondary); margin: 0; max-width: 400px; }

/* Table Container */
.table-container {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
  gap: 16px;
  flex-wrap: wrap;
}

.table-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.table-actions { display: flex; align-items: center; gap: 12px; }

.search-box { position: relative; display: flex; align-items: center; }

.search-icon {
  position: absolute;
  left: 10px;
  color: var(--text-secondary);
  pointer-events: none;
}

.search-input {
  padding: 8px 12px 8px 34px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  width: 260px;
  outline: none;
  transition: border-color 0.2s;
}

.search-input:focus { border-color: #667eea; }

.refresh-button {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.refresh-button:hover { background: var(--bg-tertiary); border-color: var(--border-secondary); }

/* Accounts Table */
.table-wrapper { overflow-x: auto; }
.desktop-view  { display: block; }
.mobile-view   { display: none; }

.accounts-table {
  width: 100%;
  border-collapse: collapse;
}

.accounts-table th {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 14px;
  padding: 16px 12px;
  text-align: left;
  border-bottom: 1px solid var(--border-primary);
  white-space: nowrap;
}

.accounts-table td {
  padding: 16px 12px;
  border-bottom: 1px solid var(--border-primary);
  color: var(--text-primary);
  font-size: 14px;
  vertical-align: top;
}

.account-row:hover { background: var(--bg-tertiary); }

.index-cell { color: var(--text-secondary); width: 40px; }

.name-info { display: flex; flex-direction: column; gap: 4px; }
.account-name { font-weight: 600; color: var(--text-primary); }
.account-id { font-size: 12px; color: var(--text-secondary); }

.id-badge {
  display: inline-block;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  font-family: monospace;
}

.namespace-text {
  font-size: 12px;
  color: var(--text-secondary);
  font-family: monospace;
  word-break: break-all;
}

.actions-cell .action-buttons { display: flex; gap: 8px; }

.action-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.action-btn:hover { background: var(--bg-tertiary); border-color: var(--border-secondary); }

.phones-btn:hover { background: #667eea; color: white; border-color: #667eea; }

/* Mobile Cards */
.account-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.account-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.account-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-tertiary);
}

.account-name-info { flex: 1; }
.account-card-header .account-name { font-size: 16px; font-weight: 600; color: var(--text-primary); margin: 0 0 4px; display: block; }
.account-card-header .account-id  { font-size: 12px; color: var(--text-secondary); }

.lead-actions { display: flex; gap: 8px; }

.mobile-action-btn {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 16px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-action-btn:hover { background: var(--bg-tertiary); border-color: var(--border-secondary); }
.mobile-action-btn.phones-btn:hover { background: #667eea; color: white; border-color: #667eea; }

.account-card-body { padding: 16px; }

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding: 4px 0;
}

.info-row:last-child { margin-bottom: 0; }

.info-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  min-width: 90px;
  flex-shrink: 0;
}

.info-value { font-size: 14px; color: var(--text-primary); word-break: break-word; }

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
  position: sticky;
  top: 0;
  background: var(--bg-primary);
  z-index: 1;
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
}

.close-button {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
}
.close-button:hover { background: var(--bg-tertiary); color: var(--text-primary); }

.modal-body { padding: 24px; flex: 1; }

.detail-group { margin-bottom: 28px; }
.detail-group:last-child { margin-bottom: 0; }

.detail-group h4 {
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-primary);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.detail-item { display: flex; flex-direction: column; gap: 4px; }

.detail-item label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item span {
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
}

.webhook-url {
  font-size: 13px !important;
  font-family: monospace;
  color: var(--text-secondary) !important;
  word-break: break-all;
  font-weight: 400 !important;
}

/* Phones table inside modal */
.phones-table {
  width: 100%;
  border-collapse: collapse;
}

.phones-table th {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 13px;
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid var(--border-primary);
  white-space: nowrap;
}

.phones-table td {
  padding: 14px 12px;
  border-bottom: 1px solid var(--border-primary);
  color: var(--text-primary);
  font-size: 14px;
  vertical-align: middle;
}

.phone-row:last-child td { border-bottom: none; }
.phone-row:hover { background: var(--bg-tertiary); }

.phone-number-cell { font-weight: 600; color: #667eea; }

/* Status badges */
.status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.badge-green  { background: rgba(16,185,129,0.1);  color: #059669; }
.badge-yellow { background: rgba(245,158,11,0.1);  color: #d97706; }
.badge-red    { background: rgba(239,68,68,0.1);   color: #dc2626; }
.badge-gray   { background: var(--bg-tertiary);    color: var(--text-secondary); }

/* Modal footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-primary);
}

.close-btn {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}
.close-btn:hover { background: var(--bg-tertiary); border-color: var(--border-secondary); }

/* Responsive */
@media (max-width: 1024px) {
  .page-header { flex-direction: column; gap: 20px; align-items: flex-start; }
  .header-stats { align-self: flex-end; }
  .table-header { flex-direction: column; gap: 12px; align-items: flex-start; }
  .table-actions { align-self: flex-end; }
}

@media (max-width: 768px) {
  .main-content { margin-left: 0; padding-top: 70px; }
  .page-content { padding: 12px; }
  .desktop-view { display: none; }
  .mobile-view  { display: block; padding: 0 16px 16px; }
  .search-input { width: 180px; }
  .modal-content { margin: 10px; max-height: calc(100vh - 20px); }
  .modal-header  { padding: 16px; }
  .modal-body    { padding: 16px; }
  .modal-footer  { padding: 16px; }
  .detail-grid   { grid-template-columns: 1fr; }
}
</style>
